import { Link, NavLink, Outlet, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  AvatarFallback,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  cn,
} from "@pracownik/ui";
import { LogOut, Settings, CreditCard, Shield, LayoutGrid, Plus } from "lucide-react";
import { CreditBadge } from "./credit-badge";
import { BrandLogo } from "./brand-logo";
import { ThemeToggle } from "./theme-toggle";
import { useAuth } from "@/providers/auth-provider";
import { useIsAdmin } from "@/lib/admin";
import { signOut } from "@/lib/auth-client";

function initials(name: string | undefined, email: string | undefined): string {
  const src = (name?.trim() || email || "?").split(/[\s@.]+/).filter(Boolean);
  return ((src[0]?.[0] ?? "") + (src[1]?.[0] ?? "")).toUpperCase() || "?";
}

export function DashboardLayout() {
  const { t } = useTranslation("dashboard");
  const { user } = useAuth();
  const { isAdmin } = useIsAdmin();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  const navLink = ({ isActive }: { isActive: boolean }) =>
    cn(
      "inline-flex h-9 items-center gap-2 rounded-lg px-3 text-sm font-medium transition-colors duration-(--dur-fast)",
      isActive
        ? "bg-accent text-foreground"
        : "text-text-secondary hover:bg-accent/70 hover:text-foreground",
    );

  return (
    <div className="min-h-dvh bg-background">
      <a href="#main" className="skip-link">
        {t("nav.skipToContent")}
      </a>
      <header className="sticky top-0 z-40 border-b bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
          <div className="flex items-center gap-6">
            <BrandLogo to="/dashboard" />
            <nav aria-label={t("nav.primary")} className="hidden items-center gap-1 md:flex">
              <NavLink to="/dashboard" end className={navLink}>
                <LayoutGrid className="size-4" />
                {t("nav.workers")}
              </NavLink>
              <NavLink to="/dashboard/topup" className={navLink}>
                <CreditCard className="size-4" />
                {t("nav.billing")}
              </NavLink>
              {isAdmin && (
                <NavLink to="/admin" className={navLink}>
                  <Shield className="size-4" />
                  {t("admin.navLabel")}
                </NavLink>
              )}
            </nav>
          </div>

          <div className="flex items-center gap-2">
            <Link to="/dashboard/topup" className="hidden sm:block">
              <CreditBadge />
            </Link>
            <Button asChild size="sm" className="hidden sm:inline-flex">
              <Link to="/dashboard/create">
                <Plus className="size-4" />
                {t("createWorker")}
              </Link>
            </Button>
            <ThemeToggle className="hidden sm:inline-flex" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={t("nav.accountMenu")}
                  className="rounded-full outline-none ring-offset-background transition-shadow focus-visible:ring-[3px] focus-visible:ring-ring/40"
                >
                  <Avatar className="size-9 border">
                    <AvatarFallback className="bg-primary-soft text-primary text-xs font-semibold">
                      {initials(user?.name, user?.email)}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="font-normal">
                  <p className="truncate text-sm font-medium text-foreground">{user?.name}</p>
                  <p className="truncate text-xs text-text-tertiary">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => navigate("/dashboard")} className="md:hidden">
                  <LayoutGrid />
                  {t("nav.workers")}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate("/dashboard/topup")}>
                  <CreditCard />
                  {t("nav.billing")}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate("/dashboard/settings")}>
                  <Settings />
                  {t("nav.settings")}
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem onSelect={() => navigate("/admin")}>
                    <Shield />
                    {t("admin.navLabel")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={handleSignOut} variant="destructive">
                  <LogOut />
                  {t("nav.signOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main id="main" className="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 sm:py-10">
        <Outlet />
      </main>
    </div>
  );
}
